import { MagnifyingGlass } from "@medusajs/icons"
import { clx, Text } from "@medusajs/ui"
import { useEffect, useMemo, useRef, useState } from "react"
import { NAV, SALES_CHANNELS } from "@/lib/nav"
import { SETTINGS_NAV } from "@/lib/settings-nav"
import { useCan, useCurrentMe } from "@/lib/permissions"
import { isAppOn } from "@/apps/registry"
import { useQuery } from "@tanstack/react-query"
import { sdk } from "@/lib/sdk"
import { formatMoney } from "@/lib/format"
import { useOrderName } from "@/lib/permissions"

type Entry = { label: string; hint: string; to: string; sub?: string; thumb?: string | null; group?: string }

/** Debounced value, so typing doesn't fire a search per keystroke. */
function useDebounced<T>(v: T, ms: number) {
  const [d, setD] = useState(v)
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms)
    return () => clearTimeout(t)
  }, [v, ms])
  return d
}

/** Live records for the search box: up to 5 products, orders and customers each. */
function useRecordResults(term: string) {
  const can = useCan()
  const orderName = useOrderName()
  const q = useDebounced(term, 200)
  const on = q.length >= 2
  const products = useQuery({
    queryKey: ["search", "products", q],
    enabled: on && can("products:read"),
    queryFn: () => sdk.admin.product.list({ q, limit: 5, fields: "id,title,thumbnail,status,+metadata" } as any),
    staleTime: 30_000,
  })
  const orders = useQuery({
    queryKey: ["search", "orders", q],
    enabled: on && can("orders:read"),
    queryFn: () => sdk.client.fetch<{ orders: any[] }>("/admin/terify/orders", { query: { q, limit: 5, offset: 0 } }),
    staleTime: 30_000,
  })
  const customers = useQuery({
    queryKey: ["search", "customers", q],
    enabled: on && can("customers:read"),
    queryFn: () => sdk.client.fetch<{ customers: any[] }>("/admin/terify/customers", { query: { q, limit: 5, offset: 0 } }),
    staleTime: 30_000,
  })
  const out: Entry[] = []
  for (const p of (products.data as any)?.products ?? []) out.push({ group: "Products", label: p.title, hint: (p.metadata as any)?.archived ? "Archived" : p.status === "published" ? "Active" : "Draft", to: `/products/${p.id}`, thumb: p.thumbnail ?? null })
  for (const o of orders.data?.orders ?? []) out.push({ group: "Orders", label: orderName(o.display_id), sub: o.customer_name || o.email || "", hint: formatMoney(o.total, o.currency_code), to: `/orders/${o.id}` })
  for (const c of customers.data?.customers ?? []) out.push({ group: "Customers", label: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email, sub: c.email, hint: `${c.orders_count ?? 0} order${c.orders_count === 1 ? "" : "s"}`, to: `/customers/${c.id}` })
  return { entries: out, loading: on && (products.isFetching || orders.isFetching || customers.isFetching), pending: term.trim() !== q }
}

/** Shopify's search: jump to any page or setting, or search a list. */
export function CommandPalette({ open, onOpenChange, onGo }: { open: boolean; onOpenChange: (o: boolean) => void; onGo: (to: string) => void }) {
  const can = useCan()
  const [q, setQ] = useState("")
  const [i, setI] = useState(0)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setQ("")
      setI(0)
      setTimeout(() => input.current?.focus(), 0)
    }
  }, [open])

  const me = useCurrentMe()
  const pages = useMemo<Entry[]>(() => {
    const out: Entry[] = []
    for (const n of [...NAV, ...SALES_CHANNELS]) {
      if (n.perm && !can(n.perm)) continue
      out.push({ label: n.label, hint: "Page", to: n.to })
      n.children?.forEach((c) => (!c.perm || can(c.perm)) && (!c.app || isAppOn(me, c.app)) && out.push({ label: c.label, hint: n.label, to: c.to }))
    }
    for (const s of SETTINGS_NAV) if (!s.perm || can(s.perm)) out.push({ label: s.label, hint: "Settings", to: s.to })
    out.push({ label: "Add product", hint: "Action", to: "/products/new" }, { label: "Create collection", hint: "Action", to: "/collections/new" })
    return out
  }, [can, me])

  const term = q.trim().toLowerCase()
  const records = useRecordResults(q.trim())
  const results: Entry[] = [
    ...records.entries,
    ...pages.filter((p) => !term || p.label.toLowerCase().includes(term) || p.hint.toLowerCase().includes(term)).slice(0, term ? 5 : 8).map((p) => ({ ...p, group: term ? "Pages and settings" : undefined })),
    ...(term
      ? ([
          can("products:read") && { group: "See all results", label: `All products matching “${q.trim()}”`, hint: "Products", to: `/products?q=${encodeURIComponent(q.trim())}` },
          can("orders:read") && { group: "See all results", label: `All orders matching “${q.trim()}”`, hint: "Orders", to: `/orders?q=${encodeURIComponent(q.trim())}` },
          can("customers:read") && { group: "See all results", label: `All customers matching “${q.trim()}”`, hint: "Customers", to: `/customers?q=${encodeURIComponent(q.trim())}` },
        ].filter(Boolean) as Entry[])
      : []),
  ]

  if (!open) return null
  const go = (e?: Entry) => {
    if (!e) return
    onOpenChange(false)
    onGo(e.to)
  }
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 px-4 pt-[12vh]" onClick={() => onOpenChange(false)}>
      <div className="w-full max-w-[560px] overflow-hidden rounded-xl bg-white shadow-elevation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-x-2 border-b px-3">
          <MagnifyingGlass className="text-ui-fg-muted" />
          <input
            ref={input}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setI(0)
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") onOpenChange(false)
              else if (e.key === "ArrowDown") (e.preventDefault(), setI((x) => Math.min(x + 1, results.length - 1)))
              else if (e.key === "ArrowUp") (e.preventDefault(), setI((x) => Math.max(x - 1, 0)))
              else if (e.key === "Enter") go(results[i])
            }}
            placeholder="Search pages, settings, products, orders…"
            data-tb-bare className="h-12 flex-1 bg-transparent text-[14px] outline-none"
          />
        </div>
        <ul className="max-h-[60vh] overflow-y-auto p-1" role="listbox" aria-label="Search results">
          {results.map((r, idx) => (
            <li key={r.to + r.label} role="option" aria-selected={idx === i}>
              {r.group && r.group !== results[idx - 1]?.group && <div className="px-3 pb-1 pt-2.5 text-[12px] font-medium text-ui-fg-muted">{r.group}</div>}
              <button
                onMouseEnter={() => setI(idx)}
                onClick={() => go(r)}
                className={clx("flex w-full items-center gap-x-3 rounded-lg px-3 py-2 text-left text-[13px]", idx === i && "bg-ui-bg-base-hover")}
              >
                {r.group === "Products" && (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-ui-border-base bg-ui-bg-subtle">
                    {r.thumb ? <img src={r.thumb} alt="" className="h-full w-full object-cover" loading="lazy" /> : null}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{r.label}</span>
                  {r.sub && <span className="block truncate text-[12px] text-ui-fg-muted">{r.sub}</span>}
                </span>
                <Text size="xsmall" className="shrink-0 text-ui-fg-muted">{r.hint}</Text>
              </button>
            </li>
          ))}
          {records.loading && !records.entries.length && <li className="px-3 py-2 text-[12px] text-ui-fg-muted">Searching…</li>}
          {!results.length && !records.loading && <li className="px-3 py-6 text-center text-[13px] text-ui-fg-muted">No results</li>}
        </ul>
      </div>
    </div>
  )
}
